if (!customElements.get("ck-dual-variant-picker")) {
  class CkDualVariantPicker extends HTMLElement {
    constructor() {
      super();
      this.lastCheckedCardValue = null;
    }

    connectedCallback() {
      this.addEventListener("change", (event) => {
        const target = event.target;
        if (target.matches('[data-role="tab-input"]')) {
          this.handleTabChange(target, event);
        } else if (target.matches('[data-role="card-input"]')) {
          this.lastCheckedCardValue = target.value;
          this.emitOptionChange(target, event);
        }
      });

      this.initSelection();
    }

    get selectedOptionValues() {
      const checked = this.querySelectorAll(
        '[data-role="tab-input"]:checked, [data-role="card-input"]:checked'
      );
      const result = [];
      checked.forEach((input) => {
        const id = input.dataset.optionValueId;
        if (id && !result.includes(id)) {
          result.push(id);
        }
      });
      return result;
    }

    handleTabChange(tabInput, event) {
      this.showPanel(tabInput.dataset.tab);
      // syncCardSelection dispatches its own change event (carrying both the
      // new tab and card values) whenever it has to move the selection. Only
      // emit here ourselves if it didn't — firing both would race two
      // overlapping product-info fetches and could leave the ATC button
      // stuck on a stale/aborted response.
      const synced = this.syncCardSelection(tabInput.dataset.tab);
      if (!synced) {
        this.emitOptionChange(tabInput, event);
      }
    }

    showPanel(tabHandle) {
      this.querySelectorAll("[data-tab-panel]").forEach((panel) => {
        panel.toggleAttribute("hidden", panel.dataset.tabPanel !== tabHandle);
      });
    }

    syncCardSelection(tabHandle) {
      const panel = this.querySelector(`[data-tab-panel="${tabHandle}"]`);
      if (!panel) return false;

      const cardInputs = panel.querySelectorAll('[data-role="card-input"]');
      let target = null;

      if (this.lastCheckedCardValue) {
        target = Array.from(cardInputs).find(
          (input) => input.value === this.lastCheckedCardValue && !input.disabled
        );
      }
      if (!target) {
        target =
          Array.from(cardInputs).find(
            (input) => input.dataset.recommended === "true" && !input.disabled
          ) || Array.from(cardInputs).find((input) => !input.disabled);
      }

      if (target && !target.checked) {
        target.checked = true;
        this.lastCheckedCardValue = target.value;
        target.dispatchEvent(new Event("change", { bubbles: true }));
        return true;
      }

      return false;
    }

    emitOptionChange(target, event) {
      MinimogEvents.emit(MinimogTheme.pubSubEvents.optionValueSelectionChange, {
        data: {
          selectedOptionValues: this.selectedOptionValues,
          event,
          target,
        },
      });
    }

    initSelection() {
      const selectedVariantScript = this.querySelector(
        "script[data-selected-variant]"
      );
      let selectedVariant = null;
      if (selectedVariantScript) {
        try {
          selectedVariant = JSON.parse(selectedVariantScript.textContent);
        } catch (e) {
          selectedVariant = null;
        }
      }

      const tabInputs = this.querySelectorAll('[data-role="tab-input"]');
      // Respect a deep link (?variant=) or a returning user's prior choice —
      // only steer toward the recommended card on a clean initial load.
      const hasDeepLink = new URLSearchParams(window.location.search).has(
        "variant"
      );

      let targetTabInput = null;
      let targetCard = null;

      if (!hasDeepLink) {
        targetCard = this.querySelector(
          '[data-role="card-input"][data-recommended="true"]:not([disabled])'
        );
        if (targetCard) {
          const recommendedPanel = targetCard.closest("[data-tab-panel]");
          targetTabInput = Array.from(tabInputs).find(
            (input) => input.dataset.tab === recommendedPanel?.dataset.tabPanel
          );
          if (!targetTabInput) {
            targetCard = null;
          }
        }
      }

      if (!targetTabInput && selectedVariant) {
        targetTabInput = Array.from(tabInputs).find(
          (input) => input.value === selectedVariant.option1
        );
      }
      // Server rendered a different variant than we're about to select (e.g.
      // Shopify's own default pick is out of stock, or we're deliberately
      // overriding it with the recommended one) — the rest of the page
      // (price, ATC button) was rendered against that server pick, so once we
      // land on a different one we need to push a sync below.
      let needsSync = !targetTabInput || !!targetCard;
      if (!targetTabInput) {
        targetTabInput = tabInputs[0];
      }
      if (!targetTabInput) return;

      targetTabInput.checked = true;
      this.showPanel(targetTabInput.dataset.tab);

      const panel = this.querySelector(
        `[data-tab-panel="${targetTabInput.dataset.tab}"]`
      );
      if (!panel) return;

      if (!targetCard) {
        const cardInputs = panel.querySelectorAll('[data-role="card-input"]');
        if (selectedVariant && !needsSync) {
          targetCard = Array.from(cardInputs).find(
            (input) => input.value === selectedVariant.option2 && !input.disabled
          );
        }
        if (!targetCard) {
          needsSync = true;
          targetCard =
            Array.from(cardInputs).find(
              (input) => input.dataset.recommended === "true" && !input.disabled
            ) || Array.from(cardInputs).find((input) => !input.disabled);
        }
      }
      if (targetCard) {
        targetCard.checked = true;
        this.lastCheckedCardValue = targetCard.value;
      }

      // Drive price / add-to-cart / URL through the normal change pipeline
      // so they reflect the variant we actually landed on, not the server's
      // initial (possibly unavailable) pick.
      if (needsSync && targetCard) {
        targetCard.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  customElements.define("ck-dual-variant-picker", CkDualVariantPicker);
}
